import { Box, Group, Stack, createStyles } from '@mantine/core';
import type { PolicyRuleIn } from '@repo/api-codegen';
import { Select, Text, TextInput } from '@repo/foundations';
import { IconButton } from '@repo/foundations/components/Buttons';
import { useCallback } from 'react';
import type { CreatePolicyStore } from './CreatePolicyStore';

const useStyles = createStyles(() => ({}));

interface PolicyConditionHeaderProps {
	rule: PolicyRuleIn;
	index: number;
	removePolicyRule: (index: number) => void;
	dqsV2: boolean;
	store: CreatePolicyStore;
	debouncedServerUpdate: (index: number, updatedRule: PolicyRuleIn) => void;
}

function PolicyConditionHeader({
	rule,
	index,
	removePolicyRule,
	dqsV2,
	store,
	debouncedServerUpdate,
}: PolicyConditionHeaderProps) {
	const { theme } = useStyles();
	const updatePolicyRule = useCallback(
		(
			newIndex: number,
			field: keyof PolicyRuleIn,
			value: string | number | boolean
		) => {
			const updatedRule = { ...store.policyRules[newIndex], [field]: value };

			// Update store immediately for responsive UI
			store.updatePolicyRule(newIndex, updatedRule);

			// Debounce server update to avoid jumping during typing
			debouncedServerUpdate(newIndex, updatedRule);
		},
		[store, debouncedServerUpdate]
	);

	return (
		<Stack spacing="md">
			<Box
				px="md"
				bg={theme.other.getColor('surface/secondary/default')}
				sx={{
					borderTopLeftRadius: theme.radius.md,
					borderTopRightRadius: theme.radius.md,
				}}
			>
				<Group position="apart" py="md">
					<Text size="sm" weight="semibold">
						Condition
					</Text>
					<IconButton
						variant="tertiary"
						size="sm"
						iconName="x"
						tooltip="Remove rule"
						onClick={() => removePolicyRule(index)}
					/>
				</Group>
			</Box>
			<Stack px="md" spacing="md">
				<TextInput
					label="Name"
					size="sm"
					placeholder="Rule name"
					value={rule.name || ''}
					onChange={(e) => updatePolicyRule(index, 'name', e.target.value)}
				/>
				{dqsV2 && store.dqsEnabled && (
					<Select
						size="sm"
						label="Impact score"
						data={[
							{ value: '5', label: '5' },
							{ value: '10', label: '10' },
							{ value: '15', label: '15' },
							{ value: '20', label: '20' },
							{ value: '25', label: '25' },
						]}
						value={String(rule.points_possible || 10)}
						onChange={(value) =>
							updatePolicyRule(index, 'points_possible', Number(value) || 10)
						}
					/>
				)}
			</Stack>
		</Stack>
	);
}

PolicyConditionHeader.displayName = 'PolicyConditionHeader';
export default PolicyConditionHeader;
